/**
 * Which coins earn over the period and which keep losing (FreqUI's
 * "Performance"): a coin that returns a loss again and again is holding slots
 * the strategy could use elsewhere. Every figure is the server's.
 */
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import type { SymbolPerformanceResponse } from "@/shared/api";
import { isNegative, signedMoney } from "@/shared/money/money";
import { humanAge } from "@/shared/time/age";

const SHOWN = 12;
const ROW = "grid grid-cols-[1fr_auto_auto] items-baseline gap-x-3 border-b border-border-subtle py-1.5 last:border-b-0 sm:grid-cols-[1fr_4rem_7rem_6rem]";

export function CoinPerformance({
  symbols,
}: {
  readonly symbols: readonly SymbolPerformanceResponse[];
}): React.JSX.Element | null {
  const [all, setAll] = useState(false);
  if (symbols.length === 0) return null;
  // The server orders best first; the worst are the reason to look, so the
  // short list keeps both ends.
  const shown =
    all || symbols.length <= SHOWN
      ? symbols
      : [...symbols.slice(0, SHOWN / 2), ...symbols.slice(-SHOWN / 2)];
  return (
    <Panel title={`Монеты · ${String(symbols.length)}`} testId="coin-performance">
      <div className={`${ROW} text-helper-sm text-text-muted`}>
        <span>Монета</span>
        <span className="text-right">Сделок</span>
        <span className="text-right">Заработано, USDT</span>
        <span className="hidden text-right sm:block">Ср. время</span>
      </div>
      {shown.map((coin) => (
        <div key={coin.symbol} className={ROW} data-testid="coin-row">
          <span className="truncate text-body-main text-text-primary">{coin.symbol}</span>
          <span className="text-right tabular-nums text-text-secondary">
            {String(coin.cycles)}
            {coin.losers > 0 && <span className="text-status-negative"> · {String(coin.losers)}−</span>}
          </span>
          <span
            className={`text-right tabular-nums ${isNegative(coin.realised_net) ? "text-status-negative" : "text-text-primary"}`}
          >
            {signedMoney(coin.realised_net)}
          </span>
          <span className="hidden text-right tabular-nums text-text-secondary sm:block">
            {coin.average_seconds == null ? "—" : humanAge(coin.average_seconds)}
          </span>
        </div>
      ))}
      {symbols.length > SHOWN && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setAll((current) => !current);
          }}
        >
          {all ? "Только лучшие и худшие" : `Все ${String(symbols.length)}`}
        </Button>
      )}
      <p className="mt-2 text-helper-sm text-text-secondary">
        За выбранный период, по закрытым сделкам. «2−» — сколько из них закрылось в минус.
      </p>
    </Panel>
  );
}
